import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [[1, 'Bugün'], [7, '7 gün'], [30, '30 gün'], [90, '90 gün'], [365, '1 yıl']];
const regionNames = typeof Intl !== 'undefined' && Intl.DisplayNames ? new Intl.DisplayNames(['tr'], { type: 'region' }) : null;
const flag = (c) => (c && c.length === 2 ? String.fromCodePoint(...[...c.toUpperCase()].map((x) => 0x1f1e6 + x.charCodeAt(0) - 65)) : '');
const countryName = (c) => {
  if (!c || c === '—') return 'Bilinmiyor';
  try { return `${flag(c)} ${regionNames ? regionNames.of(c) : c}`; } catch (e) { return c; }
};
const fmt = (d) => new Date(d).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const Kpi = ({ label, value, hint, testId }) => (
  <div className="p-5 rounded-xl bg-[#121212] border border-white/10" data-testid={testId}>
    <p className="text-xs uppercase tracking-[0.14em] text-white/40">{label}</p>
    <p className="text-3xl text-white mt-2" style={{ fontFamily: 'var(--font-display)' }}>{(value ?? 0).toLocaleString('tr-TR')}</p>
    {hint && <p className="text-xs text-white/35 mt-1">{hint}</p>}
  </div>
);

const Bars = ({ title, rows, unit = 'ziyaret', testId }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="p-5 rounded-xl bg-[#121212] border border-white/10" data-testid={testId}>
      <h3 className="text-sm font-semibold text-white/70 mb-4">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-white/30">Henüz veri yok</p> : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.name}>
              <div className="flex justify-between gap-3 text-sm mb-1">
                <span className="text-white/80 truncate" title={r.name}>{r.name}</span>
                <span className="text-white/45 whitespace-nowrap">{r.count} {unit} · {r.visitors} kişi</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5"><div className="h-full rounded-full bg-[#C8AA73]" style={{ width: `${(r.count / max) * 100}%` }} /></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AdminStats = () => {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(false);

  const sendReport = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/admin/weekly-report/send`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
      toast.success('Haftalık rapor Telegram grubuna gönderildi');
    } catch (e) { toast.error('Rapor gönderilemedi'); }
    setSending(false);
  };

  useEffect(() => {
    axios.get(`${API}/admin/analytics`, { params: { days }, headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } })
      .then((r) => setD(r.data)).catch(() => toast.error('İstatistikler yüklenemedi'));
  }, [days]);

  return (
    <div data-testid="admin-stats">
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>İstatistikler</h1>
          <p className="text-white/50 mt-2">Ziyaretçiler, trafik kaynakları (referral) ve en çok görüntülenen sayfalar</p>
        </div>
        <button onClick={sendReport} disabled={sending} className="px-3.5 py-1.5 rounded-full text-sm border border-[#C8AA73]/40 text-[#E8D6AF] hover:bg-[#C8AA73]/10 disabled:opacity-50 transition-colors" data-testid="stats-send-report-btn">{sending ? 'Gönderiliyor…' : 'Haftalık raporu şimdi gönder'}</button>
        <div className="flex flex-wrap gap-2 sm:ml-auto">
          {RANGES.map(([v, l]) => (
            <button key={v} onClick={() => setDays(v)} className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${days === v ? 'bg-[#C8AA73] text-black border-[#C8AA73]' : 'text-white/60 border-white/10 hover:text-white'}`} data-testid={`stats-range-${v}`}>{l}</button>
          ))}
        </div>
      </div>

      {!d ? <div className="py-20 text-center text-white/40">Yükleniyor…</div> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Toplam ziyaretçi" value={d.all_time.visitors} hint={`${d.all_time.views.toLocaleString('tr-TR')} sayfa görüntüleme (tüm zamanlar)`} testId="kpi-total-visitors" />
            <Kpi label="Bugün" value={d.today.visitors} hint={`${d.today.views} görüntüleme`} testId="kpi-today" />
            <Kpi label="Seçili dönem ziyaretçi" value={d.period.visitors} hint={`${d.period.sessions} oturum`} testId="kpi-period-visitors" />
            <Kpi label="Seçili dönem görüntüleme" value={d.period.views} hint={d.period.sessions ? `oturum başına ${(d.period.views / d.period.sessions).toFixed(1)} sayfa` : ''} testId="kpi-period-views" />
          </div>

          <div className="p-5 rounded-xl bg-[#121212] border border-white/10" data-testid="stats-daily-chart">
            <h3 className="text-sm font-semibold text-white/70 mb-4">Günlük ziyaret</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.daily} margin={{ left: -20, right: 8, top: 8 }}>
                  <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C8AA73" stopOpacity={0.45} /><stop offset="100%" stopColor="#C8AA73" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis allowDecimals={false} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: '#121212', border: '1px solid rgba(200,170,115,0.3)', borderRadius: 8, color: '#fff' }} />
                  <Area type="monotone" dataKey="visitors" name="Ziyaretçi" stroke="#C8AA73" fill="url(#g)" strokeWidth={2} dot={d.daily.length <= 7} />
                  <Area type="monotone" dataKey="views" name="Görüntüleme" stroke="#E8D6AF" fill="transparent" strokeDasharray="4 4" strokeWidth={1.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Bars title="Trafik kaynakları" rows={d.sources} unit="oturum" testId="stats-sources" />
            <Bars title="Ülkeler" rows={(d.countries || []).map((r) => ({ ...r, name: countryName(r.name) }))} unit="oturum" testId="stats-countries" />
            <Bars title="Referans veren sayfalar (tam adres)" rows={d.referrers} unit="oturum" testId="stats-referrers" />
            <Bars title="En çok görüntülenen sayfalar" rows={d.pages} unit="görüntüleme" testId="stats-pages" />
            <Bars title="Kampanyalar (utm_campaign)" rows={d.campaigns} unit="oturum" testId="stats-campaigns" />
            <Bars title="Cihaz" rows={d.devices} unit="görüntüleme" testId="stats-devices" />
            <Bars title="Dil" rows={d.languages.map((r) => ({ ...r, name: String(r.name).toUpperCase() }))} unit="görüntüleme" testId="stats-languages" />
          </div>

          <div className="p-5 rounded-xl bg-[#121212] border border-white/10 overflow-x-auto" data-testid="stats-recent">
            <h3 className="text-sm font-semibold text-white/70 mb-4">Son ziyaretler</h3>
            <table className="w-full text-sm min-w-[680px]">
              <thead><tr className="text-left text-white/40 text-xs uppercase tracking-wider"><th className="pb-3 font-normal">Zaman</th><th className="pb-3 font-normal">Ülke</th><th className="pb-3 font-normal">Kaynak</th><th className="pb-3 font-normal">Giriş sayfası</th><th className="pb-3 font-normal">Cihaz</th><th className="pb-3 font-normal">Dil</th></tr></thead>
              <tbody>
                {d.recent.map((r, i) => (
                  <tr key={i} className="border-t border-white/5 text-white/70">
                    <td className="py-2.5 whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="py-2.5 whitespace-nowrap">{countryName(r.country)}</td>
                    <td className="py-2.5" title={r.referrer || ''}>{r.source}{r.utm_campaign ? ` · ${r.utm_campaign}` : ''}</td>
                    <td className="py-2.5">{r.path}</td>
                    <td className="py-2.5">{r.device}</td>
                    <td className="py-2.5 uppercase">{r.language}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {d.recent.length === 0 && <p className="text-sm text-white/30">Henüz ziyaret yok</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStats;
